import { isNull } from 'lodash';
import { SectionSeparator } from '@styles/layout';
import { Destination, TripTypes } from '@model/common';
import { TemplateComponent, TabGroupComponent, ComplexComponentTypes } from '@model/content';
import { ComponentHandles, ComponentTypes } from '@util/content';
import { Logging } from '@util/logging';

interface ComponentsContainerProps {
  components: Array<TemplateComponent>;
  componentsData?: Array<any>;
  path?: string;
  tripType?: TripTypes;
  destination?: Destination;
  isWhiteLabel: boolean;
}

const WHITE_LABEL_COMPONENTS_BLACKLIST = [
  'popularHotels',
  'specialOffers',
  'exploreDestination',
  'tourGroup',
  'testimonials',
  'testimonialsComponent',
  'yourRecentlyReviewed'
];

const getRenderedComponent = (component: TemplateComponent) => {
  const { handle, ...props } = component;
  const Component = ComponentTypes.get(handle);
  if (Component) {
    return <Component {...props} />;
  } else {
    return undefined;
  }
};

const ComplexComponents = [ComponentHandles.TAB_GROUP];

export const ComponentsContainer = ({
  components,
  componentsData,
  path,
  tripType,
  destination,
  isWhiteLabel
}: ComponentsContainerProps) => {
  let hasCallToBook: boolean = false;
  let complexContainerIndex: number | null = null;

  const mapComponent = (component: TemplateComponent, idx: number) => {
    const { handle, ...rest } = component;

    // some components never show on white label sites so we filter them here
    // replacing the first instance with a call to book component and ignoring the rest
    if (isWhiteLabel && WHITE_LABEL_COMPONENTS_BLACKLIST.includes(handle)) {
      if (hasCallToBook || tripType != TripTypes.HOLIDAYS) {
        return null;
      }
      hasCallToBook = true;
      const CallToBookComponent = ComponentTypes.get(ComponentHandles.CALL_TO_BOOK);
      return (
        <>
          <SectionSeparator />
          <CallToBookComponent path={path} tripType={tripType} destination={destination} />
        </>
      );
    }

    let data: any = null;
    if (ComplexComponents.includes(handle as ComponentHandles)) {
      const { type } = component as TabGroupComponent;
      if (type === ComplexComponentTypes.OPEN) {
        complexContainerIndex = idx;
      } else if (complexContainerIndex) {
        const startIndex = complexContainerIndex + 1;
        complexContainerIndex = null;
        data = components.slice(startIndex, idx).map((component) => {
          const renderable = getRenderedComponent(component);
          return { ...component, renderable };
        });
      }
    }

    const MappedComponent = complexContainerIndex ? null : ComponentTypes.get(handle);

    if (!MappedComponent && isNull(complexContainerIndex)) {
      Logging.warn({ text: `No mapped component for ${handle || JSON.stringify(component)}` });
    }

    return MappedComponent ? (
      <MappedComponent
        {...rest}
        data={componentsData ? componentsData[idx] : data}
        path={path}
        tripType={tripType}
        destination={destination}
        key={`${handle}-${idx}`}
        className={handle}
      />
    ) : null;
  };

  return (
    <section>
      {components.map(mapComponent)}
      <SectionSeparator size={50} />
    </section>
  );
};
